/**
 * Helper puri per <birthdate-input> (giorno / mese / anno).
 *
 * Formato di scambio 'YYYY-MM-DD', lo stesso di <vb-input type="date">.
 * Il mese è 0-indexed (indice del <vb-select>), 1-indexed solo nel compose.
 * I confronti fra date sono lessicografici: su 'YYYY-MM-DD' coincidono con
 * quelli cronologici ed evitano i fusi orari di new Date().
 * Nessuna dipendenza: importare commons.js aprirebbe una catena circolare.
 */

const PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const is_date = (value) => typeof value === 'string' && PATTERN.test(value);
const is_month = (value) => value !== null && value >= 0 && value < 12;
const is_year = (value) => value !== null && value >= 1000 && value <= 9999;
const pad = (value) => `${value}`.padStart(2, '0');

export const to_integer = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  if (!isFinite(parsed) || parsed % 1 !== 0) {
    return null;
  }
  return parsed;
};

// Febbraio resta a 29 finché l'anno non è completo: i campi si compilano in
// qualsiasi ordine e il giorno viene ri-clampato quando l'anno arriva.
export const days_in_month = (year, month) => {
  const month_index = to_integer(month);
  if (!is_month(month_index)) {
    return 31;
  }
  const full_year = to_integer(year);
  if (!is_year(full_year)) {
    return month_index === 1 ? 29 : 31;
  }
  return new Date(full_year, month_index + 1, 0).getDate();
};

export const clamp_day = (day, year, month) => {
  const parsed_day = to_integer(day);
  if (parsed_day === null) {
    return null;
  }
  return Math.min(Math.max(parsed_day, 1), days_in_month(year, month));
};

export const compose_birthdate = (parts) => {
  const { day, month, year } = parts || {};
  const parsed_day = to_integer(day);
  const month_index = to_integer(month);
  const full_year = to_integer(year);
  if (parsed_day === null || !is_month(month_index) || !is_year(full_year)) {
    return null;
  }
  if (parsed_day < 1 || parsed_day > days_in_month(full_year, month_index)) {
    return null;
  }
  return `${full_year}-${pad(month_index + 1)}-${pad(parsed_day)}`;
};

// Validazione per round-trip: se il compose non ricostruisce la stringa di
// partenza, la data non esiste nel calendario (es. '1990-02-31').
export const parse_birthdate = (value) => {
  if (is_date(value)) {
    const [year, month, day] = value.split('-').map(Number);
    const parts = { day, month: month - 1, year };
    if (compose_birthdate(parts) === value) {
      return parts;
    }
  }
  return { day: null, month: null, year: null };
};

// Riporta la data al bound più vicino, come moment.min/max in <vb-input>.
// Bound assenti o malformati = illimitati; con min > max vince min.
export const clamp_to_bounds = (date, min, max) => {
  if (!is_date(date)) {
    return null;
  }
  let clamped = date;
  if (is_date(max) && clamped > max) {
    clamped = max;
  }
  if (is_date(min) && clamped < min) {
    clamped = min;
  }
  return clamped;
};

// Come il form scrive l'età dichiarata in ricerca ("1 anno" / "4 anni").
export const declared_age_label = (age) => {
  let label = `${age} anni`;
  if (age === 0) {
    label = 'meno di 1 anno';
  }
  if (age === 1) {
    label = '1 anno';
  }
  return label;
};

export const year_bounds = (min, max, today = new Date()) => {
  let min_year = 1900;
  let max_year = today.getFullYear();
  if (is_date(min)) {
    min_year = Number(min.slice(0, 4));
  }
  if (is_date(max)) {
    max_year = Number(max.slice(0, 4));
  }
  return { min: min_year, max: max_year };
};
